// Главная «/» — конфиг кита лендингов. Тексты, порядок секций и копирайт взяты
// ДОСЛОВНО из согласованного SEO-документа «Главная» (октябрь 2026): правит их
// SEO-специалист, а не вёрстка.
//
// Почему конфиг, а не своя страница: структура документа один в один ложится на
// секции кита (hero → примеры → инструменты → шаги → преимущества → стили →
// подарок → таблица «с чего начать» → цены → отзывы → SEO-текст → FAQ → CTA), а
// у кита уже есть шапка с якорями, футер с перелинковкой, липкий мобильный CTA,
// JSON-LD (включая FAQPage) и поведение поверх снимка пререндера.
//
// Цены НЕ хардкодим: секция `pricing` грузит реальные пакеты из API (как
// /balance). Из документа взяты только заголовок, подводка и CTA.
import { IconAdjustmentsHorizontal, IconDownload, IconFileMusic, IconLanguage, IconLayoutGrid, IconMoodSmile, IconMovie, IconMusic, IconPencil, IconPiano, IconSparkles, IconUserCircle, IconVersions, IconVinyl, IconWand, } from "@tabler/icons-react";
import { SOCIALS } from "./content";
export const HOME_PATH = "/";
export const homeConfig = {
    path: HOME_PATH,
    name: "Виртуоз",
    // Мета главной не из документа: их утверждает SEO-специалист отдельно.
    seo: {
        title: "Создать песню с ИИ онлайн | Создавайте песни с помощью нейросети в сервисе Виртуоз",
        description: "Создать песню с помощью нейросети онлайн за 2 минуты. Превратите идею, текст или стихи в готовый трек с инструментальным сопровождением и вокалом. Бесплатный старт, Российский сервис, оплата картой РФ, права на треки — ваши.",
    },
    theme: { mood: "studio", accent: "#dc3528", accent2: "#6e52ee" },
    hero: {
        eyebrow: "AI-студия",
        h1: "Создать песню с ИИ",
        lead: [
            "Virtuoz превращает идею, текст или стихи в готовый трек с инструментальным сопровождением и вокалом.",
            "Выберите стиль, задайте темп и вайб — ИИ подготовит трек со стемами, битом и вокалом. Забирайте в релиз, кидайте в сет или перезаписывайте, пока не сойдётся.",
        ],
        ctas: [{ label: "Создать свою песню" }, { label: "Послушать примеры", anchor: "examples" }],
        note: "Бесплатный старт · Интерфейс на русском · Музыкальные навыки не нужны",
        visual: {
            kind: "studio",
            prompt: "Летний инди-поп про поездку к морю с друзьями, тёплый женский вокал, 104 BPM",
            chips: ["Инди-поп", "104 BPM", "Женский вокал", "Тёплый вайб"],
            trackTitle: "Дорога к морю",
            trackMeta: "Версия 3 · 3:12",
            stems: ["Вокал", "Барабаны", "Бас", "Синты"],
        },
    },
    sections: [
        {
            type: "examples",
            id: "examples",
            title: "Библиотека примеров: как звучит музыка из Virtuoz",
            intro: "Нажмите на любое музыкальное произведение, чтобы включить её прямо на странице.",
        },
        {
            type: "cards",
            id: "tools",
            variant: "tools",
            title: "Что можно сделать в ИИ-студии",
            intro: "Начните с идеи или загрузите свой материал — текст, запись исполнения или готовый трек.",
            items: [
                {
                    icon: IconWand,
                    title: "ИИ-генератор песен",
                    text: "По вашему описанию ИИ подготовит слова, мелодию и исполнение в нужном стиле.",
                    cta: { label: "Создать песню" },
                },
                {
                    icon: IconFileMusic,
                    title: "Песня по тексту или стихам",
                    text: "Добавьте слова или стихи — получите мелодию, аранжировку и пение с сохранением оригинальных строк.",
                    cta: { label: "Озвучить текст" },
                },
                {
                    icon: IconPencil,
                    title: "Генератор текста",
                    text: "Задайте тему — нейросеть предложит слова и структуру будущей композиции.",
                    cta: { label: "Написать текст" },
                },
                {
                    icon: IconPiano,
                    title: "Генератор музыки с ИИ",
                    text: "Создавайте инструментальные композиции для видео, подкаста, рекламы или личного проекта.",
                    cta: { label: "Создать музыку" },
                },
                {
                    icon: IconVinyl,
                    title: "AI-кавер",
                    text: "Загрузите песню и попробуйте другой тембр, стиль или характер исполнения.",
                    cta: { label: "Сделать ИИ-кавер" },
                },
                {
                    icon: IconUserCircle,
                    title: "Песня своим голосом",
                    text: "Создайте голосовую модель и используйте её для исполнения новых композиций.",
                    cta: { label: "Добавить свою запись" },
                },
                {
                    icon: IconAdjustmentsHorizontal,
                    title: "Ремикс и переработка",
                    text: "Измените стиль, темп, инструменты, манеру пения или отдельный фрагмент.",
                    cta: { label: "Переделать трек" },
                },
            ],
        },
        {
            type: "steps",
            id: "how",
            title: "Три шага до релиза",
            items: [
                {
                    title: "Опишите идею",
                    text: "Направление, референс, темп, настроение. Хотите — добавьте свои слова, не хотите — поручите ИИ сгенерировать их по описанию.",
                },
                {
                    title: "Прогоните дубли",
                    text: "Слушайте варианты, меняйте куплет, бит или исполнение. Каждый прогон — новая версия, старые остаются. Сравните два дубля: уделите каждому хотя бы минуту, чтобы оценить мелодию и звук.",
                },
                {
                    title: "Забирайте стемы",
                    text: "WAV, мастер и стемы отдельными дорожками. Дальше — в DAW, на площадки или в сет.",
                },
            ],
        },
        {
            type: "cards",
            id: "features",
            variant: "grid",
            title: "Всё необходимое для создания музыки с ИИ",
            items: [
                {
                    icon: IconLayoutGrid,
                    title: "Всё в одном проекте",
                    text: "Создавайте аудиотреки, каверы, ремиксы и музыкальные видео без перехода между сервисами.",
                },
                {
                    icon: IconMoodSmile,
                    title: "Не нужно быть профессиональным музыкантом",
                    text: "Опишите желаемый результат обычными словами — остальное соберёт ИИ.",
                },
                {
                    icon: IconSparkles,
                    title: "Бесплатный старт",
                    text: "Попробуйте основные возможности до оплаты тарифа.",
                },
                {
                    icon: IconLanguage,
                    title: "Интерфейс полностью на русском",
                    text: "Понятный интерфейс и поддержка без языкового барьера.",
                },
                {
                    icon: IconMusic,
                    title: "Не набросок, а целый трек",
                    text: "Получите полноценную песню с музыкой, аранжировкой и вокалом.",
                },
                {
                    icon: IconVersions,
                    title: "Несколько версий на выбор",
                    text: "Сравнивайте варианты и сохраняйте тот, который ближе к вашей задумке. Не судите о результате по первым секундам: послушайте хотя бы минуту, чтобы оценить развитие композиции.",
                },
                {
                    icon: IconMovie,
                    title: "Музыка и видео",
                    text: "Создайте AI-видео для готовой записи в той же студии.",
                },
                {
                    icon: IconDownload,
                    title: "Файлы для продакшена",
                    text: "Скачивайте mp3, мастер, WAV и стемы для дальнейшей работы в DAW.",
                },
            ],
        },
        {
            type: "chips",
            id: "genres",
            title: "Создайте песню в нужном стиле",
            intro: "Выберите жанр, который подходит вашей истории, стихам или проекту. Поп или рок могут стать основой, а настроение, темп, инструменты и тип исполнения можно настроить отдельно.",
            chips: [
                "Классика",
                "Лезгинка",
                "Народная музыка",
                "Поп",
                "Рок",
                "Рэп и хип-хоп",
                "Шансон",
                "Электронная музыка и EDM",
            ],
            cta: { label: "Задать стиль и создать песню" },
        },
        {
            type: "story",
            id: "gift",
            title: "Подарите песню с вашей общей историей",
            paragraphs: [
                "Когда обычного поздравления мало, расскажите историю через музыку. Добавьте имена, общие воспоминания, важные фразы и детали характера — ИИ поможет превратить их в уникальную песню с нужным настроением и исполнением.",
                "Её можно отправить близкому человеку, включить на празднике или сохранить как музыкальную память о событии.",
            ],
            groups: [
                { label: "Для любого повода", tags: ["Свадьба", "День рождения", "Юбилей", "Новый год"] },
                { label: "Для самых близких", tags: ["Мужу", "Папе", "Любимой", "Подруге"] },
            ],
            ctas: [{ label: "Подготовить подарок" }, { label: "Послушать примеры", anchor: "examples" }],
        },
        {
            type: "compare",
            id: "path",
            title: "С чего начать работу с ИИ",
            intro: "Не подстраивайте идею под инструмент. Отталкивайтесь от того, что у вас уже есть, — и сразу переходите к созданию.",
            head: ["У меня есть", "Что использовать", "Что я получу"],
            rows: [
                ["Идея или пара фраз", "Создать песню с нуля", "Цельную песню с текстом, музыкой и вокалом"],
                ["Свои стихи или слова", "Озвучить свой текст", "Готовую запись с вашими авторскими строками без изменений"],
                ["Задумка без вокала", "Создать инструментал", "Музыку для видео, подкаста, рекламы или проекта"],
                ["Готовая песня", "Сделать AI-кавер", "Новую версию с другим тембром, жанром или подачей"],
                ["Запись своего исполнения", "Спеть с помощью голосовой модели", "Новый трек с вашей голосовой моделью"],
                ["Трек, который хочется улучшить", "Сделать ремикс", "Новое звучание или переработанные фрагменты"],
            ],
            cta: { label: "Создать свой трек" },
        },
        {
            type: "pricing",
            id: "pricing",
            title: "Цены",
            intro: "Попробовать студию можно бесплатно. Если нужно больше генераций, подберите подходящий пакет.",
            cta: { label: "Попробовать бесплатно" },
        },
        {
            type: "reviews",
            id: "reviews",
            title: "Что говорят пользователи",
            items: [
                {
                    text: "За вечер сделал трек, который до этого месяц лежал в заметках. Описал настроение, выбрал вариант — и уже через 10 минут слушал готовую демку.",
                    name: "Максим",
                    role: "Музыкант · инди-проект",
                },
                {
                    text: "Раньше искала музыку для Reels по полчаса. Теперь просто пишу, какой нужен вайб, и получаю трек под видео за пару минут.",
                    name: "Аня",
                    role: "Контент-креатор",
                },
                {
                    text: "Я вообще не умею писать музыку. Вбил пару строк про наш офис — получил результат, который потом неделю скидывали друг другу в рабочем чате.",
                    name: "Илья",
                    role: "Маркетолог",
                },
                {
                    text: "Сделал джингл для рекламы без студии, композитора и бесконечных правок. Получилось быстрее, чем написать нормальное ТЗ подрядчику.",
                    name: "Роман",
                    role: "Владелец бренда",
                },
                {
                    text: "Использую AI-генератор как напарника: закидываю идею, слушаю 5–6 вариантов и забираю удачные ходы в свои треки.",
                    name: "Денис",
                    role: "Битмейкер",
                },
                {
                    text: "Нужен был трек для ролика клиента к утру. В 2 ночи сделал три версии, утром отправил на выбор. Одну согласовали сразу.",
                    name: "Кирилл",
                    role: "Моушн-дизайнер",
                },
                {
                    text: "Самое кайфовое — можно менять настроение, вокал или отдельные части, а не начинать всё заново. Реально ощущается как работа с музыкантом.",
                    name: "Саша",
                    role: "Автор YouTube-канала",
                },
                {
                    text: "С друзьями ради прикола сделали песню про нашу поездку. Через полчаса уже слушали её в машине. Теперь на каждую тусовку генерим новый трек.",
                    name: "Лера",
                    role: "Студентка",
                },
            ],
        },
        {
            type: "article",
            id: "about",
            title: "Что такое ИИ-генератор песен",
            paragraphs: [
                "ИИ-генератор песен — это онлайн-сервис, который создаёт музыку и вокал по текстовому описанию, готовым словам или загруженному аудиоматериалу. Пользователь задаёт исходную идею и параметры звучания, прослушивает варианты и выбирает версию для скачивания или дальнейшей доработки.",
                "В Virtuoz можно начать с короткого описания, собственного текста, стихов, записи исполнения или существующей композиции. Набор доступных инструментов зависит от режима: ИИ может помочь со словами, создать инструментальное сопровождение, подготовить вокальную версию, сделать кавер или переработать отдельные части.",
                "Результат зависит от качества исходного материала, точности запроса, жанра и выбранной версии. Поэтому полезно прослушать несколько вариантов, уточнить настройки и отдельно проверить слова, произношение и условия использования перед публикацией.",
            ],
        },
        {
            type: "faq",
            id: "faq",
            title: "Часто задаваемые вопросы",
            items: [
                {
                    q: "Как создать песню с ИИ?",
                    a: "Откройте ИИ-генератор песен, опишите тему, направление и настроение, затем запустите генерацию. Можно начать с двух фраз или добавить свой текст. Прослушайте результат, выберите лучший для вашей задумки вариант и доработайте его в студии.",
                },
                {
                    q: "Можно ли создать песню только по идее?",
                    a: "Да. Опишите сюжет, настроение и желаемое направление — например, поп или рок. ИИ подготовит слова, сопровождение и исполнение. Чем точнее важные детали, тем проще направить результат в нужную сторону.",
                },
                {
                    q: "Можно ли вставить свой текст или стихи без изменений?",
                    a: "Да, для этого откройте режим «Песня по тексту» с сохранением исходных слов. Перед генерацией проверьте каждый куплет, припев и границы между ними, а также убедитесь, что у вас есть право использовать этот материал.",
                },
                {
                    q: "Как написать слова с помощью искусственного интеллекта?",
                    a: "Откройте редактор текста и опишите тему, историю, героев и настроение. Virtuoz предложит структуру, куплеты и припев. Любой из этих фрагментов можно отредактировать, а затем передать в генератор песни.",
                },
                {
                    q: "Можно ли сделать инструментальную музыку без вокала?",
                    a: "Да. Откройте соответствующий режим и укажите, что нужен инструментал. Добавьте жанр, темп, настроение и предполагаемую задачу — например, фон для видео, подкаста или рекламы.",
                },
                {
                    q: "Как работает AI-кавер?",
                    a: "Вы загружаете готовую песню и задаёте новый тембр, направление или характер звучания. ИИ создаёт другую версию на основе исходного материала. Для любых загружаемых записей и голосов необходимы права на использование и согласия.",
                },
                {
                    q: "Можно ли спеть своим голосом?",
                    a: "Да, если функция доступна в выбранном тарифе. Нужно записать материал по инструкции и подтвердить согласие на создание голосовой модели. Использовать чужой голос без разрешения нельзя.",
                },
                {
                    q: "Чем ремикс отличается от AI-кавера?",
                    a: "Это два способа переработки: AI-кавер меняет прежде всего подачу готовой песни — например, тембр или манеру исполнения. Ремикс предназначен для более широких изменений: можно менять темп, инструменты, структуру, пение или отдельные части композиции.",
                },
                {
                    q: "Можно ли использовать результат в коммерческих целях и выпускать на площадках?",
                    a: "Это зависит от лицензии выбранного тарифа и прав на загруженные материалы. Перед рекламным использованием или дистрибуцией проверьте оферту Virtuoz и правила конкретной площадки.",
                },
            ],
        },
        // Секции «Где нас найти» в документе нет — но боты в Telegram/MAX/VK и
        // поддержка были на главной и остаются её рабочей точкой входа. Удалять
        // живой канал ради молчания документа не стали, см. отчёт.
        {
            type: "socials",
            id: "social",
            title: "Где нас найти",
            intro: "боты, чаты и поддержка",
            items: SOCIALS,
        },
        {
            type: "cta",
            title: "Первая песня — уже сегодня",
            intro: "Опишите желаемый результат обычными словами — остальное соберёт ИИ.",
            cta: { label: "Создать песню" },
        },
    ],
};
